/**
 * Lightweight network status pub/sub bridge.
 *
 * The Axios response interceptor calls `reportNetworkError()` when it detects
 * `ERR_NETWORK` (no response received) and `reportNetworkSuccess()` on every
 * successful response. UI components subscribe via `subscribeToOffline` /
 * `subscribeToOnline` — no polling, no dependencies beyond Axios itself.
 *
 * Only genuine state transitions fire notifications:
 *   online  → offline  first ERR_NETWORK after a period of success.
 *   offline → online   first successful response after an ERR_NETWORK.
 *
 * This means repeated network errors while already offline (or repeated
 * successes while already online) are silently ignored.
 */

type NetworkListener = () => void;

/** Module-level perceived connectivity — true until proven otherwise. */
let networkOnline = true;

const offlineListeners = new Set<NetworkListener>();
const onlineListeners = new Set<NetworkListener>();

/**
 * Called by the Axios response interceptor when a request receives no HTTP
 * response and Axios sets `error.code = 'ERR_NETWORK'`.
 * Notifies all offline subscribers on the first such call.
 */
export const reportNetworkError = (): void => {
  if (networkOnline) {
    networkOnline = false;
    offlineListeners.forEach((fn) => fn());
  }
};

/**
 * Called by the Axios response interceptor on every successful HTTP response.
 * Notifies all online subscribers only when recovering from an offline state.
 */
export const reportNetworkSuccess = (): void => {
  if (!networkOnline) {
    networkOnline = true;
    onlineListeners.forEach((fn) => fn());
  }
};

/**
 * Subscribe to the offline transition (online → offline).
 * Returns an unsubscribe function — call it inside a `useEffect` cleanup.
 *
 * @param fn - Callback invoked on the first `ERR_NETWORK` error.
 * @returns Unsubscribe function.
 */
export const subscribeToOffline = (fn: NetworkListener): (() => void) => {
  offlineListeners.add(fn);
  return (): void => {
    offlineListeners.delete(fn);
  };
};

/**
 * Subscribe to the online recovery transition (offline → online).
 * Returns an unsubscribe function — call it inside a `useEffect` cleanup.
 *
 * @param fn - Callback invoked on the first successful response after ERR_NETWORK.
 * @returns Unsubscribe function.
 */
export const subscribeToOnline = (fn: NetworkListener): (() => void) => {
  onlineListeners.add(fn);
  return (): void => {
    onlineListeners.delete(fn);
  };
};

/** Returns the current perceived connectivity. */
export const getNetworkOnline = (): boolean => networkOnline;

/**
 * Resets module state. **For unit tests only** — do not call in application code.
 * @internal
 */
export const _resetForTests = (): void => {
  networkOnline = true;
  offlineListeners.clear();
  onlineListeners.clear();
};
